import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useReducedMotion } from "framer-motion";
import confetti from "canvas-confetti";
import type { Mission } from "@/lib/api/controlPlaneOnboardingClient";
import { ICON_MAP } from "./OnboardingWidgetParts";

// ── Confetti helper ────────────────────────────────────────────────

export function fireCelebrationConfetti(iconElement: HTMLElement | null) {
  let x: number, y: number;

  if (iconElement) {
    const rect = iconElement.getBoundingClientRect();
    x = (rect.left + rect.width / 2) / window.innerWidth;
    y = (rect.top + rect.height / 2) / window.innerHeight;
  } else {
    x = 30 / window.innerWidth;
    y = (window.innerHeight - 30) / window.innerHeight;
  }

  confetti({
    particleCount: 30,
    spread: 60,
    origin: { x, y },
    colors: ["#3b82f6", "#6366f1", "#f59e0b"],
    gravity: 1.2,
    ticks: 90,
    disableForReducedMotion: true,
  });
}

// ── Animated counter hook ──────────────────────────────────────────

export function useAnimatedCounter(target: number, duration = 400) {
  const [display, setDisplay] = useState(target);
  const prefersReducedMotion = useReducedMotion();
  const prevTarget = useRef(target);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (prevTarget.current === target) return;
    const from = prevTarget.current;
    prevTarget.current = target;

    if (prefersReducedMotion) {
      setDisplay(target);
      return;
    }

    const start = performance.now();
    function animate(now: number) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + (target - from) * eased));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    }
    rafRef.current = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration, prefersReducedMotion]);

  return display;
}

// ── Celebration Toast ──────────────────────────────────────────────

export function CelebrationToast({ mission }: { mission: Mission }) {
  const IconComponent = ICON_MAP[mission.icon];
  return (
    <motion.div
      initial={{ opacity: 0, x: -10, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: -10, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="fixed bottom-6 left-20 z-50
        bg-white dark:bg-gray-900
        border border-gray-200 dark:border-gray-700
        shadow-lg rounded px-4 py-3
        flex items-center gap-3
        max-w-[280px]"
      role="status"
      aria-live="polite"
    >
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
        {IconComponent ? (
          <IconComponent size={16} className="text-green-600 dark:text-green-400" />
        ) : (
          <Check size={16} className="text-green-600 dark:text-green-400" />
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
          {mission.title}!
        </p>
        <p className="text-xs text-green-600 dark:text-green-400 font-medium">
          +{mission.credits_reward} credits
        </p>
      </div>
    </motion.div>
  );
}
